import type { TrackerConfig, UserIdentity, Event, ClickTrackingConfig, RetryConfig } from "./types";
import { generateSessionId, getSessionId, setSessionId } from "./utils/session";
import {
  getCurrentUrl,
  getReferrer,
  getUserAgent,
  getDeviceContext,
} from "./utils/browser";
import { getScreenDimensions, getViewportDimensions } from "./utils/dimensions";
import {
  getAnonymousId,
  getIdentityInfo,
  linkIdentity,
} from "./utils/identity";
import { EngagementTracker } from "./utils/engagement";
import {
  JourneyTracker,
  AttributionTracker,
  FunnelTracker,
  type FunnelDefinition,
} from "./utils/journey";
import { WalletDetector } from "./utils/wallet-detection";

/**
 * WaveTrace Tracker - Enhanced user tracking with comprehensive identification
 */
export class WaveTracker {
  private config: Required<Omit<TrackerConfig, 'autoTrackClicks' | 'retry'>> & {
    autoTrackClicks: boolean;
    retry: Required<RetryConfig>;
  };
  private clickTrackingConfig: ClickTrackingConfig;
  private eventQueue: Event[] = [];
  private userIdentity: UserIdentity = {};
  private sessionId: string;
  private anonymousId: string;
  private batchTimer: number | null = null;
  private clickListener: ((e: MouseEvent) => void) | null = null;

  // Enhanced tracking
  private engagementTracker: EngagementTracker | null = null;
  private journeyTracker: JourneyTracker | null = null;
  private funnelTracker: FunnelTracker | null = null;
  private deviceContext: any = null;
  private initPromise: Promise<void>;

  // Auto-wallet detection
  private walletDetector: WalletDetector | null = null;

  // Offline support
  private isOnline: boolean = true;
  private onlineListener: (() => void) | null = null;
  private offlineListener: (() => void) | null = null;
  private beforeUnloadListener: (() => void) | null = null;

  constructor(config: TrackerConfig) {
    // Validate required config
    if (!config.endpoint) {
      throw new Error("WaveTracker: endpoint is required");
    }

    // Normalize click tracking config
    const clickConfig = config.autoTrackClicks ?? true;
    const autoTrackClicks = typeof clickConfig === 'boolean' ? clickConfig : true;

    this.clickTrackingConfig = typeof clickConfig === 'object'
      ? {
          mode: clickConfig.mode ?? 'important',
          customSelectors: clickConfig.customSelectors,
          ignoreSelectors: clickConfig.ignoreSelectors,
        }
      : { mode: 'important' }; // Default to 'important' mode for DApps

    // Set defaults
    this.config = {
      endpoint: config.endpoint,
      autoTrackPageViews: config.autoTrackPageViews ?? true,
      autoTrackClicks,
      batchSize: config.batchSize ?? 10,
      batchTimeout: config.batchTimeout ?? 5000,
      includeReferrer: config.includeReferrer ?? true,
      includeUserAgent: config.includeUserAgent ?? true,
      customHeaders: config.customHeaders ?? {},
      debug: config.debug ?? false,
      enableEngagement: config.enableEngagement ?? true,
      enableJourney: config.enableJourney ?? true,
      includeDeviceContext: config.includeDeviceContext ?? true,
      autoDetectWallet: config.autoDetectWallet ?? false,
      offlineQueue: config.offlineQueue ?? true,
      maxOfflineEvents: config.maxOfflineEvents ?? 100,
      retry: {
        enabled: config.retry?.enabled ?? true,
        maxRetries: config.retry?.maxRetries ?? 3,
        initialDelay: config.retry?.initialDelay ?? 1000,
        backoffMultiplier: config.retry?.backoffMultiplier ?? 2,
      },
    };

    // Initialize session and anonymous tracking
    this.sessionId = getSessionId() || generateSessionId();
    setSessionId(this.sessionId);
    this.anonymousId = getAnonymousId();

    // Store first-touch attribution
    AttributionTracker.storeFirstTouch();

    this.log("WaveTracker initialized", this.config);

    // Initialize async features
    this.initPromise = this.initializeAsync();

    // Initialize engagement tracking
    if (this.config.enableEngagement) {
      this.engagementTracker = new EngagementTracker();
      this.log("Engagement tracking enabled");
    }

    // Initialize journey tracking
    if (this.config.enableJourney) {
      this.journeyTracker = new JourneyTracker();
      this.funnelTracker = new FunnelTracker();
      this.log("Journey tracking enabled");
    }

    // Auto-track page views
    if (this.config.autoTrackPageViews) {
      // Wait for initialization before tracking
      this.initPromise.then(() => this.trackPageView());
    }

    // Auto-track clicks
    if (this.config.autoTrackClicks) {
      this.enableClickTracking();
    }

    // Load persisted offline queue
    if (this.config.offlineQueue) {
      this.loadOfflineQueue();
      this.setupOfflineSupport();
    }

    // Auto-detect wallet connections
    if (this.config.autoDetectWallet) {
      this.setupWalletDetection();
    }
  }

  /**
   * Initialize async features (device context)
   */
  private async initializeAsync(): Promise<void> {
    try {
      // Get device context if enabled
      if (this.config.includeDeviceContext) {
        this.deviceContext = await getDeviceContext();
        this.log("Device context collected", this.deviceContext);
      }
    } catch (error) {
      this.log("Error initializing async features", error);
    }
  }

  /**
   * Setup auto-wallet detection
   */
  private setupWalletDetection(): void {
    this.walletDetector = new WalletDetector(
      // On wallet connect
      (walletInfo) => {
        this.connectWallet(walletInfo.address, {
          properties: {
            chainId: walletInfo.chainId,
            walletType: walletInfo.walletType,
            autoDetected: true,
          },
        });
      },
      // On wallet disconnect
      () => {
        this.disconnectWallet();
      },
      // On chain change
      (chainId) => {
        this.track("network_changed", {
          chainId,
          wallet: this.userIdentity.wallet,
        });
      }
    );

    this.walletDetector.start();
    this.log("Auto-wallet detection enabled");
  }

  /**
   * Load offline queue from localStorage
   */
  private loadOfflineQueue(): void {
    try {
      const stored = localStorage.getItem("wt_offline_queue");
      if (stored) {
        const queue = JSON.parse(stored);
        if (Array.isArray(queue)) {
          this.eventQueue = queue.slice(0, this.config.maxOfflineEvents);
          localStorage.removeItem("wt_offline_queue");
          this.log(`Loaded ${this.eventQueue.length} events from offline queue`);
        }
      }
    } catch (error) {
      this.log("Failed to load offline queue", error);
    }
  }

  /**
   * Save offline queue to localStorage
   */
  private saveOfflineQueue(): void {
    if (!this.config.offlineQueue) return;

    try {
      const queueToSave = this.eventQueue.slice(0, this.config.maxOfflineEvents);
      localStorage.setItem("wt_offline_queue", JSON.stringify(queueToSave));
      this.log(`Saved ${queueToSave.length} events to offline queue`);
    } catch (error) {
      this.log("Failed to save offline queue", error);
    }
  }

  /**
   * Setup online/offline support
   */
  private setupOfflineSupport(): void {
    if (typeof window === "undefined") return;

    // Track online/offline status
    this.isOnline = navigator.onLine;

    // Listen to online event
    this.onlineListener = () => {
      this.isOnline = true;
      this.log("Back online - flushing queue");
      this.sendEvents(); // Flush queue when back online
    };
    window.addEventListener("online", this.onlineListener);

    // Listen to offline event
    this.offlineListener = () => {
      this.isOnline = false;
      this.log("Gone offline - will queue events");
    };
    window.addEventListener("offline", this.offlineListener);

    // Save queue before page unload
    this.beforeUnloadListener = () => {
      this.saveOfflineQueue();
    };
    window.addEventListener("beforeunload", this.beforeUnloadListener);

    this.log("Offline support enabled");
  }

  /**
   * Set user identification information
   */
  identify(identity: UserIdentity): void {
    // For DApps: wallet address is the primary identifier
    // For traditional apps: userId is the primary identifier
    const primaryId = identity.wallet || identity.userId;

    // Link anonymous ID to authenticated user
    if (primaryId && this.anonymousId) {
      const previousAnonymousId = linkIdentity(primaryId);
      this.log("Linking anonymous user to identified user", {
        anonymousId: previousAnonymousId,
        identifier: primaryId,
        type: identity.wallet ? "wallet" : "userId",
      });
    }

    this.userIdentity = { ...this.userIdentity, ...identity };
    this.log("User identified", this.userIdentity);

    // Track identification event
    this.track("user_identified", {
      previousAnonymousId: this.anonymousId,
      identifierType: identity.wallet ? "wallet" : "userId",
      ...identity,
    });
  }

  /**
   * Connect wallet for DApp tracking (convenience method)
   * This is the preferred method for DApps as it uses wallet as the primary identifier
   */
  async connectWallet(
    walletAddress: string,
    additionalData?: Omit<UserIdentity, "wallet">
  ): Promise<void> {
    if (!walletAddress) {
      throw new Error("Wallet address is required");
    }

    // Normalize wallet address (lowercase for consistency)
    const normalizedWallet = walletAddress.toLowerCase();

    // Build complete identity with wallet as primary identifier
    const identity: UserIdentity = {
      wallet: normalizedWallet,
      ...additionalData,
    };

    // Use identify to link and track
    this.identify(identity);

    // Track specific wallet_connected event for DApp analytics
    await this.track("wallet_connected", {
      wallet: normalizedWallet,
      previousAnonymousId: this.anonymousId,
      chainId: additionalData?.properties?.chainId,
      walletType: additionalData?.properties?.walletType, // e.g., 'metamask', 'walletconnect'
      ...additionalData?.properties,
    });
  }

  /**
   * Disconnect wallet (for DApps)
   */
  async disconnectWallet(): Promise<void> {
    const previousWallet = this.userIdentity.wallet;

    if (previousWallet) {
      // Track disconnection before clearing
      await this.track("wallet_disconnected", {
        wallet: previousWallet,
      });
    }

    // Clear wallet from identity but keep other user data
    this.userIdentity = {
      ...this.userIdentity,
      wallet: undefined,
    };

    this.log("Wallet disconnected", { previousWallet });
  }

  /**
   * Manually track a click on an element (useful for programmatic tracking)
   * @param element The element that was clicked
   * @param additionalProperties Additional properties to include in the event
   */
  async trackClick(element: HTMLElement, additionalProperties?: Record<string, any>): Promise<void> {
    const dataTrack = element.getAttribute('data-track');

    const event = await this.buildEventData({
      type: "click",
      properties: {
        tagName: element.tagName.toLowerCase(),
        id: element.id || undefined,
        className: element.className || undefined,
        href: (element as HTMLAnchorElement).href || undefined,
        text: element.textContent?.trim().substring(0, 100) || undefined,
        dataTrack: dataTrack || undefined,
        manual: true, // Flag to indicate this was manually tracked
        ...additionalProperties,
      },
    });

    this.addEvent(event);

    // Add to journey
    if (this.journeyTracker) {
      this.journeyTracker.addEvent({
        type: "click",
        name: dataTrack || `Clicked ${element.tagName.toLowerCase()}`,
        url: getCurrentUrl(),
        timestamp: event.timestamp,
        properties: event.properties,
      });
    }
  }

  /**
   * Build comprehensive event data with all enhanced tracking
   */
  private async buildEventData(baseEvent: Partial<Event>): Promise<Event> {
    // Wait for async initialization
    await this.initPromise;

    const event: any = {
      ...baseEvent,
      timestamp: new Date().toISOString(),
      url: getCurrentUrl(),
      title: document.title,
      referrer: this.config.includeReferrer ? getReferrer() : undefined,
      userAgent: this.config.includeUserAgent ? getUserAgent() : undefined,
      screen: getScreenDimensions(),
      viewport: getViewportDimensions(),
      sessionId: this.sessionId,
      anonymousId: this.anonymousId,
    };

    // Add user identity if available
    if (Object.keys(this.userIdentity).length > 0) {
      event.user = this.userIdentity;
    }

    // Add device context if enabled
    if (this.deviceContext) {
      event.context = this.deviceContext;
    }

    // Add engagement metrics if enabled
    if (this.engagementTracker) {
      event.engagement = this.engagementTracker.getMetrics();
    }

    // Add journey information if enabled
    if (this.journeyTracker) {
      const journeySummary = this.journeyTracker.getSummary();
      event.journey = {
        eventCount: journeySummary.totalEvents,
        pagesVisited: journeySummary.pagesVisited,
        durationMinutes: journeySummary.durationMinutes,
      };
    }

    // Add attribution data
    const attribution = AttributionTracker.getAttribution();
    const firstTouch = AttributionTracker.getFirstTouch();
    event.attribution = {
      ...attribution,
      firstTouch,
    };

    return event as Event;
  }

  /**
   * Track a page view
   */
  async trackPageView(properties?: Record<string, any>): Promise<void> {
    const event = await this.buildEventData({
      type: "pageview",
      properties,
    });

    this.addEvent(event);

    // Add to journey
    if (this.journeyTracker) {
      this.journeyTracker.addEvent({
        type: "pageview",
        name: document.title,
        url: getCurrentUrl(),
        timestamp: event.timestamp,
        properties,
      });
    }
  }

  /**
   * Track a custom event
   */
  async track(
    eventName: string,
    properties?: Record<string, any>
  ): Promise<void> {
    const event = await this.buildEventData({
      type: eventName,
      properties,
    });

    this.addEvent(event);

    // Add to journey
    if (this.journeyTracker) {
      this.journeyTracker.addEvent({
        type: "custom",
        name: eventName,
        url: getCurrentUrl(),
        timestamp: event.timestamp,
        properties,
      });
    }
  }

  /**
   * Track a blockchain transaction with automatic lifecycle tracking
   * Tracks: initiated, pending, executed, and failed states
   *
   * @param eventName Base name for the event (e.g., 'swap', 'stake')
   * @param txFunction Async function that returns a transaction
   * @param properties Additional properties to include
   * @returns The transaction result
   *
   * @example
   * const tx = await tracker.trackTransaction(
   *   'swap',
   *   async () => contract.swap(params),
   *   { fromToken: 'ETH', toToken: 'USDC', amount: '1.5' }
   * );
   */
  async trackTransaction<T extends { hash?: string; wait?: () => Promise<any> }>(
    eventName: string,
    txFunction: () => Promise<T>,
    properties?: Record<string, any>
  ): Promise<T> {
    const startTime = Date.now();

    try {
      // Track initiation
      await this.track(`${eventName}_initiated`, {
        ...properties,
        timestamp: new Date().toISOString(),
      });

      // Execute transaction
      const result = await txFunction();

      // Track pending (if transaction has a hash)
      if (result && result.hash) {
        await this.track(`${eventName}_pending`, {
          ...properties,
          txHash: result.hash,
          submittedAt: new Date().toISOString(),
        });

        // Wait for confirmation if wait() method exists
        if (typeof result.wait === 'function') {
          try {
            const receipt = await result.wait();

            // Track success
            await this.track(`${eventName}_executed`, {
              ...properties,
              txHash: result.hash,
              blockNumber: receipt.blockNumber,
              gasUsed: receipt.gasUsed?.toString(),
              confirmationTime: Date.now() - startTime,
              confirmedAt: new Date().toISOString(),
            });
          } catch (waitError: any) {
            // Transaction was submitted but failed on-chain
            await this.track(`${eventName}_failed`, {
              ...properties,
              txHash: result.hash,
              error: waitError.message,
              errorCode: waitError.code,
              failedAt: new Date().toISOString(),
            });
            throw waitError;
          }
        }
      }

      return result;
    } catch (error: any) {
      // Track failure (transaction not submitted)
      await this.track(`${eventName}_failed`, {
        ...properties,
        error: error.message,
        errorCode: error.code,
        errorReason: error.reason,
        failedAt: new Date().toISOString(),
      });
      throw error;
    }
  }

  /**
   * Define a funnel for conversion tracking
   */
  defineFunnel(funnel: FunnelDefinition): void {
    if (!this.funnelTracker) {
      this.log("Funnel tracking not enabled");
      return;
    }

    this.funnelTracker.defineFunnel(funnel);
    this.log("Funnel defined", funnel);
  }

  /**
   * Track funnel step completion
   */
  async trackFunnelStep(
    funnelName: string,
    stepName: string,
    properties?: Record<string, any>
  ): Promise<void> {
    if (!this.funnelTracker) {
      this.log("Funnel tracking not enabled");
      return;
    }

    const result = this.funnelTracker.trackStep(
      funnelName,
      stepName,
      properties
    );

    if (result) {
      await this.track("funnel_step_completed", {
        funnelName: result.funnelName,
        stepName: result.stepName,
        stepIndex: result.stepIndex,
        progress: result.progress,
        isComplete: result.isComplete,
        nextStep: result.nextStep,
        ...properties,
      });

      if (result.isComplete) {
        await this.track("funnel_completed", {
          funnelName: result.funnelName,
          ...properties,
        });
      }
    }
  }

  /**
   * Get detailed user identity information
   */
  getUserIdentity() {
    const identityInfo = getIdentityInfo();
    return {
      ...identityInfo,
      authenticated: this.userIdentity,
      sessionId: this.sessionId,
    };
  }

  /**
   * Get current engagement metrics
   */
  getEngagement() {
    return this.engagementTracker?.getMetrics() || null;
  }

  /**
   * Get user journey
   */
  getJourney() {
    return this.journeyTracker?.getJourney() || null;
  }

  /**
   * Get journey summary
   */
  getJourneySummary() {
    return this.journeyTracker?.getSummary() || null;
  }

  /**
   * Get the trackable element (finds button/link parent if clicked on child element)
   */
  private getTrackableElement(element: HTMLElement): HTMLElement {
    // If element has data-track, use it
    if (element.hasAttribute('data-track')) {
      return element;
    }

    // Check for closest trackable parent
    const closestTrackable = element.closest('button, a, [role="button"], [data-track]');
    if (closestTrackable) {
      return closestTrackable as HTMLElement;
    }

    return element;
  }

  /**
   * Check if an element should be tracked based on click tracking configuration
   */
  private shouldTrackClick(element: HTMLElement): boolean {
    // Check ignore selectors first
    if (this.clickTrackingConfig.ignoreSelectors) {
      for (const selector of this.clickTrackingConfig.ignoreSelectors) {
        try {
          if (element.matches(selector)) {
            return false;
          }
          // Also check if any parent matches the ignore selector
          if (element.closest(selector)) {
            return false;
          }
        } catch (e) {
          this.log(`Invalid ignore selector: ${selector}`, e);
        }
      }
    }

    const mode = this.clickTrackingConfig.mode;

    // Mode: all - track everything (except ignored)
    if (mode === 'all') {
      return true;
    }

    // Mode: data-attribute - only track elements with data-track attribute
    if (mode === 'data-attribute') {
      return element.hasAttribute('data-track') || element.closest('[data-track]') !== null;
    }

    // Mode: important - track buttons, links, and data-track elements
    if (mode === 'important') {
      const tagName = element.tagName.toLowerCase();

      // Track buttons and links
      if (tagName === 'button' || tagName === 'a') {
        return true;
      }

      // Track elements with role="button"
      if (element.getAttribute('role') === 'button') {
        return true;
      }

      // Track elements with data-track attribute
      if (element.hasAttribute('data-track') || element.closest('[data-track]') !== null) {
        return true;
      }

      // Track input buttons
      if (tagName === 'input') {
        const inputType = (element as HTMLInputElement).type;
        if (inputType === 'button' || inputType === 'submit') {
          return true;
        }
      }

      // Check if element is inside a button or link
      if (element.closest('button, a, [role="button"]')) {
        return true;
      }

      return false;
    }

    // Mode: custom - use custom selectors
    if (mode === 'custom' && this.clickTrackingConfig.customSelectors) {
      for (const selector of this.clickTrackingConfig.customSelectors) {
        try {
          if (element.matches(selector) || element.closest(selector)) {
            return true;
          }
        } catch (e) {
          this.log(`Invalid custom selector: ${selector}`, e);
        }
      }
      return false;
    }

    // Default: don't track
    return false;
  }

  /**
   * Enable automatic click tracking
   */
  private enableClickTracking(): void {
    this.clickListener = async (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      // Check if this click should be tracked
      if (!this.shouldTrackClick(target)) {
        return;
      }

      // Get the trackable element (could be the target or a parent)
      const trackableElement = this.getTrackableElement(target);

      // Get data-track attribute for custom event naming
      const dataTrack = trackableElement.getAttribute('data-track');

      const event = await this.buildEventData({
        type: "click",
        properties: {
          tagName: trackableElement.tagName.toLowerCase(),
          id: trackableElement.id || undefined,
          className: trackableElement.className || undefined,
          href: (trackableElement as HTMLAnchorElement).href || undefined,
          text: trackableElement.textContent?.trim().substring(0, 100) || undefined,
          dataTrack: dataTrack || undefined, // Custom identifier from data-track
          x: e.clientX,
          y: e.clientY,
        },
      });

      this.addEvent(event);

      // Add to journey
      if (this.journeyTracker) {
        this.journeyTracker.addEvent({
          type: "click",
          name: `Clicked ${target.tagName.toLowerCase()}`,
          url: getCurrentUrl(),
          timestamp: event.timestamp,
          properties: event.properties,
        });
      }
    };

    document.addEventListener("click", this.clickListener, true);
    this.log("Click tracking enabled");
  }

  /**
   * Disable automatic click tracking
   */
  disableClickTracking(): void {
    if (this.clickListener) {
      document.removeEventListener("click", this.clickListener, true);
      this.clickListener = null;
      this.log("Click tracking disabled");
    }
  }

  /**
   * Add event to queue and trigger send if needed
   */
  private addEvent(event: Event): void {
    this.eventQueue.push(event);
    this.log("Event added to queue", event);

    // Send immediately if batch size is 0
    if (this.config.batchSize === 0) {
      this.sendEvents();
      return;
    }

    // Send if queue reaches batch size
    if (this.eventQueue.length >= this.config.batchSize) {
      this.sendEvents();
      return;
    }

    // Schedule batch send
    this.scheduleBatchSend();
  }

  /**
   * Schedule a batch send using timeout
   */
  private scheduleBatchSend(): void {
    if (this.batchTimer !== null) return;

    this.batchTimer = window.setTimeout(() => {
      this.sendEvents();
      this.batchTimer = null;
    }, this.config.batchTimeout);
  }

  /**
   * Send all queued events to the endpoint
   */
  private async sendEvents(): Promise<void> {
    if (this.eventQueue.length === 0) return;

    // Don't send if offline - queue will be persisted
    if (!this.isOnline && this.config.offlineQueue) {
      this.log("Offline - events will be queued");
      this.saveOfflineQueue();
      return;
    }

    const events = [...this.eventQueue];
    this.eventQueue = [];

    // Clear batch timer
    if (this.batchTimer !== null) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    // Send with retry logic
    if (this.config.retry.enabled) {
      await this.sendEventsWithRetry(events);
    } else {
      // Simple send without retry
      await this.sendEventsSimple(events);
    }
  }

  /**
   * Send events without retry (simple mode)
   */
  private async sendEventsSimple(events: Event[]): Promise<void> {
    try {
      this.log("Sending events", events);

      const response = await fetch(this.config.endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...this.config.customHeaders,
        },
        body: JSON.stringify({ events }),
        keepalive: true,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      this.log("Events sent successfully");
    } catch (error) {
      this.log("Error sending events", error);
      // Re-add events to queue on failure
      this.eventQueue.unshift(...events);
    }
  }

  /**
   * Send events with exponential backoff retry
   */
  private async sendEventsWithRetry(
    events: Event[],
    retryCount = 0
  ): Promise<void> {
    try {
      this.log(`Sending events (attempt ${retryCount + 1})`, events);

      const response = await fetch(this.config.endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...this.config.customHeaders,
        },
        body: JSON.stringify({ events }),
        keepalive: true,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      this.log("Events sent successfully");
    } catch (error) {
      this.log(`Error sending events (attempt ${retryCount + 1})`, error);

      // Check if we should retry
      if (retryCount < this.config.retry.maxRetries) {
        // Calculate delay with exponential backoff
        const delay =
          this.config.retry.initialDelay *
          Math.pow(this.config.retry.backoffMultiplier, retryCount);

        this.log(`Retrying in ${delay}ms...`);

        // Wait and retry
        await new Promise((resolve) => setTimeout(resolve, delay));
        await this.sendEventsWithRetry(events, retryCount + 1);
      } else {
        // Max retries reached - re-add to queue
        this.log("Max retries reached - events will be queued");
        this.eventQueue.unshift(...events);

        // Save to offline queue if enabled
        if (this.config.offlineQueue) {
          this.saveOfflineQueue();
        }
      }
    }
  }

  /**
   * Manually flush all queued events
   */
  async flush(): Promise<void> {
    await this.sendEvents();
  }

  /**
   * Clean up tracker resources
   */
  destroy(): void {
    this.disableClickTracking();

    if (this.batchTimer !== null) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    // Cleanup wallet detector
    if (this.walletDetector) {
      this.walletDetector.stop();
    }

    // Cleanup offline listeners
    if (this.onlineListener) {
      window.removeEventListener("online", this.onlineListener);
    }
    if (this.offlineListener) {
      window.removeEventListener("offline", this.offlineListener);
    }
    if (this.beforeUnloadListener) {
      window.removeEventListener("beforeunload", this.beforeUnloadListener);
    }

    // Save offline queue before destroying
    if (this.config.offlineQueue && this.eventQueue.length > 0) {
      this.saveOfflineQueue();
    }

    // Cleanup engagement tracker
    if (this.engagementTracker) {
      this.engagementTracker.destroy();
    }

    // Send remaining events
    this.sendEvents();

    this.log("WaveTracker destroyed");
  }

  /**
   * Debug logging
   */
  private log(message: string, data?: any): void {
    if (this.config.debug) {
      console.log(`[WaveTracker] ${message}`, data !== undefined ? data : "");
    }
  }
}
